import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import {
	ArrowRightIcon,
	EvFlexRow,
	EvIcon,
	EvIconButton,
	EvSelectNew,
	EvTag,
	OptionType,
	TAG_STATUS,
	X
} from '@evinced-private/ui-common';

import { TestRowSharedType } from '../helpers/types';
import { useLabelsManagement } from '../hooks';
import { MODAL_DESCRIPTIONS, MODALS_LABELS } from '../labels';

import { AddLabelTestsModalProps } from './AddLabelTestsModal.types';

import './AddLabelTestsModal.scss';

export const AddLabelTestsModal: FC<AddLabelTestsModalProps> = ({
	testRows,
	tenantConfiguredLabels,
	onDataChange
}) => {
	const [ariaLiveState, setAriaLiveState] = useState<string>('');
	const labelTypeSelectWrapperRef = useRef<HTMLDivElement>(null);
	const {
		selectedLabelType,
		selectedValue,
		additions,
		labelTypeOptions,
		valueOptions,
		removeAddition,
		setSelectedLabelType,
		setSelectedValue
	} = useLabelsManagement<TestRowSharedType>({ testRows, tenantConfiguredLabels });

	useEffect(() => {
		onDataChange({
			additions
		});
	}, [additions, onDataChange]);

	const handleRemoveAddition = useCallback(
		(index: number, labelTypeLabel: string, valueLabel: string) => {
			removeAddition(index);
			setAriaLiveState(`${labelTypeLabel} with value ${valueLabel} removed from selection`);
		},
		[removeAddition]
	);

	const handleLabelTypeChange = useCallback(
		(values: OptionType[]) => {
			setSelectedLabelType(values);
		},
		[setSelectedLabelType]
	);

	const handleValueChange = useCallback(
		(values: OptionType[]) => {
			setSelectedValue(values);
			if (values.length > 0 && selectedLabelType.length > 0) {
				setAriaLiveState(`${selectedLabelType[0].label} set to ${values[0].label}`);
				// Move focus to label type dropdown since value dropdown will be disabled
				setTimeout(() => {
					const selectElement =
						labelTypeSelectWrapperRef.current?.querySelector<HTMLElement>('#label-type-select');
					selectElement?.focus();
				}, 0);
			}
		},
		[selectedLabelType, setSelectedValue]
	);

	return (
		<>
			<div aria-live="polite" aria-atomic="true" className="sr-only">
				{ariaLiveState}
			</div>
			<div className="add-label-tests-modal__content">
				<p id="add-label-tests-modal-description" className="add-label-tests-modal__description">
					{MODAL_DESCRIPTIONS.add}
				</p>

				<div className="add-label-tests-modal__selects-container">
					<div className="add-label-tests-modal__select-wrapper" ref={labelTypeSelectWrapperRef}>
						<EvSelectNew
							id="label-type-select"
							label="Choose Label Type"
							options={labelTypeOptions}
							selectedValue={selectedLabelType}
							onSelectChange={handleLabelTypeChange}
							placeholder={MODALS_LABELS.ADD_LABEL_TYPE_SELECT_PLACEHOLDER}
							listboxWidth="100%"
							shouldSortOptions={true}
						/>
					</div>

					<div className="add-label-tests-modal__arrow-separator">
						<EvIcon icon={ArrowRightIcon} />
					</div>

					<div className="add-label-tests-modal__select-wrapper">
						<EvSelectNew
							id="value-select"
							label="Value"
							options={valueOptions}
							selectedValue={selectedValue}
							onSelectChange={handleValueChange}
							placeholder={MODALS_LABELS.ADD_LABEL_VALUE_SELECT_PLACEHOLDER}
							listboxWidth="100%"
							isDisabled={selectedLabelType.length === 0}
							shouldSortOptions={true}
						/>
					</div>
				</div>

				{additions.length > 0 && (
					<>
						<div className="add-label-tests-modal__divider" />

						<div className="add-label-tests-modal__additions-section">
							<h3 className="add-label-tests-modal__additions-title">Labels to add</h3>
							<div className="add-label-tests-modal__additions-list">
								{additions.map((addition, index) => (
									<EvFlexRow
										key={`${addition.labelType.value}-${addition.value.value}`}
										align="CENTER"
										gap="SMALL"
										padding="SMALL"
									>
										<EvTag
											status={TAG_STATUS.INFO}
											text={`${addition.labelType.label}: ${addition.value.label}`}
											className="add-label-tests-modal__addition-tag"
										/>

										<EvIconButton
											icon={X}
											ariaLabel={`Remove from selection Add: ${addition.labelType.label} with value ${addition.value.label}`}
											onClick={() =>
												handleRemoveAddition(index, addition.labelType.label, addition.value.label)
											}
										/>
									</EvFlexRow>
								))}
							</div>
						</div>
					</>
				)}
			</div>
		</>
	);
};
